// SPDX-License-Identifier: AGPL-3.0-or-later

import ChatInputSettings from '@app/features/messaging/state/ChatInputSettings';
import {convertToAlphabet} from '@app/features/messaging/utils/AlphabetConversion';
import AlphabetMode from '@app/features/messaging/utils/AlphabetMode';
import {convertEmoticonsToEmoji} from '@app/features/messaging/utils/EmoticonConversionUtils';
import {EMOTE_CONTENT_PREFIX} from '@app/features/messaging/utils/MessageEmoteUtils';
import {parseSilentMessagePrefix} from '@app/features/messaging/utils/SilentMessagePrefix';
import {maybeSanitizeOutgoingMessage} from '@app/features/messaging/utils/UrlSanitizationUtils';
import {MessageFlags} from '@fluxer/constants/src/ChannelConstants';
import type {
	AllowedMentions,
	MessageReference,
	MessageStickerItem,
} from '@fluxer/schema/src/domains/message/MessageResponseSchemas';
import {hasVisibleContent as hasVisibleMessageContent} from '@fluxer/schema/src/primitives/MessageValidators';

const DEFAULT_ALLOWED_MENTIONS: AllowedMentions = {replied_user: true};

export {hasVisibleContent as hasVisibleMessageContent} from '@fluxer/schema/src/primitives/MessageValidators';

export interface ApiAttachmentMetadata {
	id: string;
	filename: string;
	upload_filename?: string;
	file_size?: number;
	content_type?: string;
	title: string;
	description?: string;
	flags?: number;
	duration?: number;
	waveform?: string;
}

interface ApiAttachmentReferenceMetadata {
	id: string;
	filename?: string;
	title?: string | null;
	description?: string | null;
	flags?: number;
}

export type ApiMessageEditAttachmentMetadata = ApiAttachmentMetadata | ApiAttachmentReferenceMetadata;

export interface MessageCreateRequest {
	content?: string | null;
	nonce?: string;
	attachments?: Array<ApiAttachmentMetadata>;
	allowed_mentions?: AllowedMentions;
	message_reference?: MessageReference;
	flags?: number;
	favorite_meme_id?: string;
	sticker_ids?: Array<string>;
	role_offers?: Array<string>;
	tts?: true;
}

export interface MessageEditRequest {
	content?: string;
	attachments?: Array<ApiMessageEditAttachmentMetadata>;
	allowed_mentions?: AllowedMentions;
	flags?: number;
	role_offers?: Array<string>;
}

export interface MessageEditPayload {
	content?: string;
	attachments?: Array<ApiMessageEditAttachmentMetadata>;
	allowedMentions?: AllowedMentions;
	flags?: number;
	roleOffers?: Array<string>;
}

export interface MessageCreatePayload {
	content?: string | null;
	nonce?: string;
	attachments?: Array<ApiAttachmentMetadata>;
	allowedMentions?: AllowedMentions;
	messageReference?: MessageReference;
	flags?: number;
	favoriteMemeId?: string;
	stickers?: Array<MessageStickerItem>;
	roleOffers?: Array<string>;
	tts?: boolean;
}

export interface NormalizedMessageContent {
	content: string;
	flags: number;
}

export function normalizeMessageContent(content: string): NormalizedMessageContent {
	const {content: withoutPrefixes, flags} = stripMessageContentPrefixes(content);
	const converted = applyOutgoingEmoticonConversion(withoutPrefixes);
	const alphabet = applyAlphabetConversion(converted);
	const sanitized = maybeSanitizeOutgoingMessage(alphabet);
	const normalizedContent = hasVisibleMessageContent(sanitized) ? sanitized : '';
	return {content: normalizedContent, flags};
}

export function canSubmitMessage(content: string, hasNonTextContent: boolean): boolean {
	return hasNonTextContent || normalizeMessageContent(content).content.length > 0;
}

export interface ComposerSubmitSignals {
	inputDisabled: boolean;
	isSubmissionBlockedBySlowmode: boolean;
	isOverCharacterLimit: boolean;
	hasMessageContent: boolean;
	hasAttachments: boolean;
	hasPendingSticker: boolean;
	isEditingMessageOnMobile: boolean;
}

export function canSubmitComposerContent(signals: ComposerSubmitSignals): boolean {
	if (signals.inputDisabled || signals.isSubmissionBlockedBySlowmode || signals.isOverCharacterLimit) {
		return false;
	}
	return (
		signals.hasMessageContent || signals.hasAttachments || signals.hasPendingSticker || signals.isEditingMessageOnMobile
	);
}

export function getComposerMessageContent(content: string, isEditingMessageOnMobile: boolean): string {
	if (isEditingMessageOnMobile) {
		return content;
	}
	const silentPrefix = parseSilentMessagePrefix(content);
	return silentPrefix == null ? content : content.slice(silentPrefix.end);
}

export function normalizeMessageEditContent(content: string): string {
	return applyAlphabetConversion(applyOutgoingEmoticonConversion(content));
}

export function buildMessageCreateRequest(payload: MessageCreatePayload): MessageCreateRequest {
	const {
		content,
		nonce,
		attachments,
		allowedMentions,
		messageReference,
		flags,
		favoriteMemeId,
		stickers,
		roleOffers,
		tts,
	} = payload;
	const requestBody: MessageCreateRequest = {};
	if (content != null && hasVisibleMessageContent(content)) {
		requestBody.content = content;
	}
	if (nonce != null) {
		requestBody.nonce = nonce;
	}
	if (attachments?.length) {
		requestBody.attachments = attachments;
	}
	if (messageReference != null || shouldIncludeAllowedMentions(allowedMentions)) {
		requestBody.allowed_mentions = allowedMentions;
	}
	if (messageReference) {
		requestBody.message_reference = messageReference;
	}
	if (flags != null) {
		requestBody.flags = flags;
	}
	if (favoriteMemeId) {
		requestBody.favorite_meme_id = favoriteMemeId;
	}
	if (stickers?.length) {
		requestBody.sticker_ids = stickers.map((sticker) => sticker.id);
	}
	if (roleOffers) {
		requestBody.role_offers = roleOffers;
	}
	if (tts) {
		requestBody.tts = true;
	}
	return requestBody;
}

export function buildMessageEditRequest(payload: MessageEditPayload): MessageEditRequest {
	const {content, attachments, allowedMentions, flags, roleOffers} = payload;
	const requestBody: MessageEditRequest = {};
	if (content !== undefined) {
		requestBody.content = normalizeMessageEditContent(content);
	}
	if (attachments !== undefined) {
		requestBody.attachments = attachments;
	}
	if (allowedMentions !== undefined) {
		requestBody.allowed_mentions = allowedMentions;
	}
	if (flags !== undefined) {
		requestBody.flags = flags;
	}
	if (roleOffers !== undefined) {
		requestBody.role_offers = roleOffers;
	}
	return requestBody;
}

/** Strips one `@silent` and one `@emote ` prefix in either order and returns matching flags. */
function stripMessageContentPrefixes(content: string): NormalizedMessageContent {
	let remaining = content;
	let flags = 0;
	let sawSilent = false;
	let sawEmote = false;
	for (;;) {
		if (!sawSilent) {
			const silentPrefix = parseSilentMessagePrefix(remaining);
			if (silentPrefix != null) {
				remaining = remaining.slice(silentPrefix.end);
				flags |= MessageFlags.SUPPRESS_NOTIFICATIONS;
				sawSilent = true;
				continue;
			}
		}
		if (!sawEmote && remaining.startsWith(EMOTE_CONTENT_PREFIX)) {
			remaining = remaining.slice(EMOTE_CONTENT_PREFIX.length);
			flags |= MessageFlags.EMOTE;
			sawEmote = true;
			continue;
		}
		break;
	}
	return {content: remaining, flags};
}
const applyOutgoingEmoticonConversion = (content: string): string => {
	return ChatInputSettings.convertEmoticons ? convertEmoticonsToEmoji(content) : content;
};
const applyAlphabetConversion = (content: string): string => {
	return AlphabetMode.sendEnabled ? convertToAlphabet(content) : content;
};
const shouldIncludeAllowedMentions = (allowedMentions?: AllowedMentions): boolean => {
	if (!allowedMentions) {
		return false;
	}
	const allowedKeys = Object.keys(allowedMentions) as Array<keyof AllowedMentions>;
	if (allowedKeys.length !== Object.keys(DEFAULT_ALLOWED_MENTIONS).length) {
		return true;
	}
	for (const key of allowedKeys) {
		if (allowedMentions[key] !== DEFAULT_ALLOWED_MENTIONS[key]) {
			return true;
		}
	}
	return false;
};
